import { Alert, Box, Button, Flex, Input } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { updateUserEmail } from '@/services/user-service';

const formSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

export type EmailUpdateFormData = z.infer<typeof formSchema>;

export interface EmailUpdateFormProps {
  onSuccess?: () => void;
  onSubmitting?: (isSubmitting: boolean) => void;
  showActions?: boolean; // Whether to show action buttons
}

export interface EmailUpdateFormRef {
  submit: () => void;
}

const EmailUpdateForm = forwardRef<EmailUpdateFormRef, EmailUpdateFormProps>(
  ({ onSuccess, onSubmitting, showActions = false }, ref) => {
    const formRef = useRef<HTMLFormElement>(null);
    const queryClient = useQueryClient();
    const { refreshCurrentUser: fetchCurrentUser } = useAuth();
    const { t } = useTranslation();

    const {
      register,
      handleSubmit,
      reset,
      formState: { errors, isDirty, isValid },
    } = useForm<EmailUpdateFormData>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        email: '',
        password: '',
      },
      mode: 'onChange',
    });

    const mutation = useMutation({
      mutationFn: async (data: EmailUpdateFormData) => {
        return await updateUserEmail({ email: data.email, password: data.password });
      },
      onSuccess: async () => {
        await fetchCurrentUser();
        queryClient.invalidateQueries({ queryKey: ['user'] });

        reset();

        toaster.success({
          title: t('settings.emailUpdate.successTitle'),
          description: t('settings.emailUpdate.successDescription'),
        });

        onSuccess?.();
      },
      onError: (error) => {
        console.error('Failed to update email:', error);
        toaster.error({
          title: t('settings.emailUpdate.errorTitle'),
          description: t('settings.emailUpdate.errorDescription'),
        });
      },
    });

    useEffect(() => {
      onSubmitting?.(mutation.isPending);
    }, [onSubmitting, mutation.isPending]);

    const onSubmit = (data: EmailUpdateFormData) => {
      mutation.mutate(data);
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        formRef.current?.requestSubmit();
      },
    }));

    return (
      <Box as="form" ref={formRef} onSubmit={handleSubmit(onSubmit)} maxW="640px" w="full">
        <Alert.Root status="warning" mb={4} rounded="md">
          <Alert.Indicator />
          <Alert.Description>{t('settings.emailUpdate.warning')}</Alert.Description>
        </Alert.Root>

        <Field label={t('settings.emailUpdate.newEmail')} errorText={errors.email?.message} mb={4}>
          <Input
            {...register('email')}
            type="email"
            variant="outline"
            placeholder={t('settings.emailUpdate.newEmailPlaceholder')}
          />
        </Field>

        <Field
          label={t('settings.emailUpdate.currentPassword')}
          errorText={errors.password?.message}
          mb={4}
        >
          <Input
            {...register('password')}
            type="password"
            variant="outline"
            placeholder={t('settings.emailUpdate.currentPasswordPlaceholder')}
          />
        </Field>

        {showActions && (
          <Flex gap={2} mt={4}>
            <Button
              type="submit"
              size="sm"
              loading={mutation.isPending}
              disabled={!isDirty || !isValid}
            >
              {t('common.update')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!isDirty || mutation.isPending}
              onClick={() => reset()}
            >
              {t('common.cancel')}
            </Button>
          </Flex>
        )}
      </Box>
    );
  }
);

EmailUpdateForm.displayName = 'EmailUpdateForm';

export default EmailUpdateForm;
